import JSZip from "jszip";
import { unixTimeSecondsToUtcTimestamp } from "../../dataimport/dateconv";
import { unique } from "../../unique";
import { createEmptyLogsAndVars, dwReportError, recordVariable } from "../../diagnostics/clientErrors";
export class InstagramProfileMetadataExtractor {
    static dataSource = "instagram";
    async extract(zipFiles) {
        const zipFile = zipFiles[0];
        const jszip = new JSZip();
        const logsAndVars = createEmptyLogsAndVars("InstagramProfileMetadataExtractor:extract");
        try {
            const result = {};
            const zip = await jszip.loadAsync(zipFile);
            recordVariable(logsAndVars, "zip", zip);
            const accountInfo = await this.extractAccountInformation(zip);
            recordVariable(logsAndVars, "accountInfo", accountInfo);
            result.instagramProfile = accountInfo;
            recordVariable(logsAndVars, "result", result);
            return result;
        }
        catch (error) {
            const baseMessage = `Error extracting metadata from instagram profile ZIP file "${zipFile.name}"`;
            if (error instanceof Error) {
                const message = `${baseMessage}: ${error.message}`;
                console.error(message);
                dwReportError(null, undefined, error, { logsAndVars, errorMessage: message });
                alert(message);
            }
            else {
                const message = `${baseMessage}: ${JSON.stringify(error)}`;
                console.error(message);
                dwReportError(null, undefined, error, { logsAndVars, errorMessage: message });
                alert(message);
            }
            return undefined;
        }
    }
    extractValue(json, propertyName) {
        if (propertyName && !(propertyName in json)) {
            return null;
        }
        const property = propertyName ? json[propertyName] : json;
        if ("href" in property && property["href"]) {
            return property["href"];
        }
        else if ("value" in property && property["value"]) {
            const value = property["value"];
            if (value.toLowerCase() === "true") {
                return true;
            }
            else if (value.toLowerCase() === "false") {
                return false;
            }
            else {
                return property["value"];
            }
        }
        else if ("timestamp" in property) {
            return unixTimeSecondsToUtcTimestamp(property["timestamp"]);
        }
        else {
            return null;
        }
    }
    /**
     * Fix the wrong unicode encodings in the instagram JSON files (each byte of a unicode symbol
     * is encoded as \u00xx, instead of using a single \uxxxx entity for the Basic Multilingual Plane
     * and then resorting to UTF-8 surrogate pairs (\uxxxx\uxxxx).
     * )
     * @param encodedStr String representation of a wrongly encoded instagram JSON file.
     * @returns UTF-8 string with decoded unicode entities.
     */
    decodeUtf8String(encodedStr) {
        const byteArray = [];
        let i = 0;
        while (i < encodedStr.length) {
            if (encodedStr[i] === "\\" && encodedStr[i + 1] === "u" && i + 5 < encodedStr.length) {
                // Extract the \uxxxx sequence
                const hexValue = encodedStr.substring(i + 2, i + 6);
                const byte = parseInt(hexValue, 16);
                byteArray.push(byte);
                i += 6; // Move past the \uxxxx sequence
            }
            else {
                // Add the unescaped character
                byteArray.push(encodedStr.charCodeAt(i));
                i++;
            }
        }
        // Create a buffer from the byte array
        const buffer = new Uint8Array(byteArray);
        // Decode the buffer to a UTF-8 string
        const decoder = new TextDecoder("utf-8");
        return decoder.decode(buffer);
    }
    async getJson(zip, jsonRelPath) {
        //console.log(`Reading JSON file: ${jsonRelPath}`);
        const jsonFile = zip.file(jsonRelPath);
        if (!jsonFile) {
            console.warn(`${jsonRelPath} not found in uploaded instagram data dump. Corresponding information cannot be extracted!`);
            return {};
        }
        try {
            const content = await jsonFile.async("text");
            const contentDecoded = this.decodeUtf8String(content);
            const json = JSON.parse(contentDecoded);
            return json;
        }
        catch (error) {
            const baseMessage = `Error parsing JSON file: ${jsonRelPath}`;
            if (error instanceof Error) {
                throw new Error(`${baseMessage}: ${error.message}`);
            }
            else {
                throw new Error(`${baseMessage}: ${JSON.stringify(error)}`);
            }
        }
    }
    toCamelCase(input) {
        // Split the input string by spaces
        const words = input.split(" ");
        // Convert the first word to lowercase
        const firstWord = words[0].toLowerCase();
        // Convert the remaining words to capitalized form
        const capitalizedWords = words.slice(1).map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
        // Concatenate the first word with the capitalized words
        const camelCaseToken = firstWord + capitalizedWords.join("");
        return camelCaseToken;
    }
    extractObject(json) {
        const result = {};
        for (const property in json) {
            const propertyCamelCase = this.toCamelCase(property);
            result[propertyCamelCase] = this.extractValue(json, property);
        }
        return result;
    }
    extractProfileChange(json) {
        const result = {};
        result.propertyName = json["Changed"]["value"];
        result.previousValue = json["Previous Value"]["value"];
        result.newValue = json["New Value"]["value"];
        result.changeDate = this.extractValue(json, "Change Date");
        return result;
    }
    extractSignupInformation(json) {
        const result = {};
        result.username = this.extractValue(json, "Username");
        result.ipAddress = this.extractValue(json, "IP Address");
        result.time = this.extractValue(json, "Time");
        result.email = json["Email"]["value"];
        result.phoneNumber = json["Phone Number"]["value"];
        result.device = json["Device"]["value"];
        return result;
    }
    /**
     * Extract account information:
     *
     * @param zip JSZip instance.
     * @returns Object with the above mentioned properties.
     */
    async extractAccountInformation(zip) {
        const result = {};
        // read personal_information/personal_information/personal_information.json
        const personalInfoJson = await this.getJson(zip, "personal_information/personal_information/personal_information.json");
        const personalInfoStringMapData = personalInfoJson["profile_user"][0]["string_map_data"];
        result.phoneNumber = this.extractValue(personalInfoStringMapData, "Phone Number");
        result.isPhoneConfirmed = this.extractValue(personalInfoStringMapData, "Phone Confirmed");
        result.phoneConfirmationMethod = this.extractValue(personalInfoStringMapData, "Phone Confirmation Method");
        result.username = this.extractValue(personalInfoStringMapData, "Username");
        result.name = this.extractValue(personalInfoStringMapData, "Name");
        result.gender = this.extractValue(personalInfoStringMapData, "Gender");
        result.dateOfBirth = this.extractValue(personalInfoStringMapData, "Date of birth");
        const accountInfoJson = await this.getJson(zip, "personal_information/personal_information/account_information.json");
        const accountInfoStringMapData = accountInfoJson["profile_account_insights"][0]["string_map_data"];
        result.firstCountryCode = this.extractValue(accountInfoStringMapData, "First Country Code");
        result.hasSharedLiveVideo = this.extractValue(accountInfoStringMapData, "Has Shared Live Video");
        result.lastLogin = this.extractValue(accountInfoStringMapData, "Last Login");
        result.firstStoryTime = this.extractValue(accountInfoStringMapData, "First Story Time");
        result.lastStoryTime = this.extractValue(accountInfoStringMapData, "Last Story Time");
        result.firstCloseFriendsStoryTime = this.extractValue(accountInfoStringMapData, "First Close Friends Story Time");
        // read personal_information/information_about_you/possible_emails.json
        const possibleEmailsJson = await this.getJson(zip, "personal_information/information_about_you/possible_emails.json");
        const possibleEmailsInferredEmails = possibleEmailsJson["inferred_data_inferred_emails"];
        const inferredEmails = [];
        for (const inferredEmailJson of possibleEmailsInferredEmails) {
            const inferredEmail = this.extractValue(inferredEmailJson["string_list_data"][0]);
            inferredEmails.push(inferredEmail);
        }
        result.inferredEmails = unique(inferredEmails);
        // read connections/followers_and_following/blocked_accounts.json
        const blockedAccountsJson = await this.getJson(zip, "connections/followers_and_following/blocked_accounts.json");
        const relationshipsBlockedUsers = blockedAccountsJson["relationships_blocked_users"];
        result.blockedAccountCount = relationshipsBlockedUsers.length;
        // read connections/followers_and_following/followers_1.json
        const followersJson = await this.getJson(zip, "connections/followers_and_following/followers_1.json");
        result.followerCount = followersJson.length;
        // read connections/followers_and_following/following.json
        const followingJson = await this.getJson(zip, "connections/followers_and_following/following.json");
        const followingJsonRelationshipsFollowing = followingJson["relationships_following"];
        result.followingCount = followingJsonRelationshipsFollowing.length;
        // read connections/followers_and_following/removed_suggestions.json
        const removedSuggestionsJson = await this.getJson(zip, "connections/followers_and_following/removed_suggestions.json");
        const relationshipsDismissedSuggestedUsers = removedSuggestionsJson["relationships_dismissed_suggested_users"];
        result.dismissedSuggestedUsers = relationshipsDismissedSuggestedUsers.length;
        // read preferences/your_topics/your_topics.json
        const yourTopicsJson = await this.getJson(zip, "preferences/your_topics/your_topics.json");
        const yourTopicsJsonTopicsYourTopics = yourTopicsJson["topics_your_topics"];
        const topics = [];
        for (const topicNode of yourTopicsJsonTopicsYourTopics) {
            const stringMapData = topicNode["string_map_data"];
            const topic = this.extractValue(stringMapData, "Name");
            topics.push(topic);
        }
        result.yourTopics = topics;
        // read personal_information/personal_information/profile_changes.json
        const profileChangesJson = await this.getJson(zip, "personal_information/personal_information/profile_changes.json");
        const profileChanges = profileChangesJson["profile_profile_change"];
        result.profileChanges = [];
        profileChanges.map((x) => {
            result.profileChanges.push(this.extractProfileChange(x["string_map_data"]));
        });
        // read preferences/media_settings/comments_allowed_from.json
        const commentsAllowedFromJson = await this.getJson(zip, "preferences/media_settings/comments_allowed_from.json");
        const commentsAllowedFromStringMapData = commentsAllowedFromJson["settings_allow_comments_from"][0]["string_map_data"];
        result.commentsAllowedFrom = this.extractValue(commentsAllowedFromStringMapData, "Comments Allowed From");
        // read preferences/media_settings/use_cross-app_messaging.json
        const useCrossAppMessagingJson = await this.getJson(zip, "preferences/media_settings/use_cross-app_messaging.json");
        const settingsUpgradedToCrossAppMessaging = useCrossAppMessagingJson["settings_upgraded_to_cross_app_messaging"][0]["string_map_data"];
        result.upgradedToCrossAppMessaging = this.extractValue(settingsUpgradedToCrossAppMessaging, "Upgraded To Cross-App Messaging");
        // read personal_information/device_information/devices.json
        const devicesJson = await this.getJson(zip, "personal_information/device_information/devices.json");
        const devicesJsonDevicesDevices = devicesJson["devices_devices"];
        const devices = [];
        for (const topicNode of devicesJsonDevicesDevices) {
            const stringMapData = topicNode["string_map_data"];
            devices.push({
                lastLogin: this.extractValue(stringMapData, "Last Login"),
                userAgent: this.extractValue(stringMapData, "User Agent"),
            });
        }
        result.devices = devices;
        // read personal_information/information_about_you/account_based_in.json
        const accountBasedInJson = await this.getJson(zip, "personal_information/information_about_you/account_based_in.json");
        const inferredDataPrimaryLocationStringMapData = accountBasedInJson["inferred_data_primary_location"][0]["string_map_data"];
        result.inferredPrimaryLocation = {
            City: this.extractValue(inferredDataPrimaryLocationStringMapData, "City Name"),
        };
        // read security_and_login_information/login_and_account_creation/last_known_location.json
        const lastKnownLocationJson = await this.getJson(zip, "security_and_login_information/login_and_account_creation/last_known_location.json");
        const accountHistoryImpreciseLastKnownLocationStringMapData = lastKnownLocationJson["account_history_imprecise_last_known_location"][0]["string_map_data"];
        result.impreciseLastKnownLocation = {
            impreciseLatitude: this.extractValue(accountHistoryImpreciseLastKnownLocationStringMapData, "Imprecise Latitude"),
            impreciseLongitude: this.extractValue(accountHistoryImpreciseLastKnownLocationStringMapData, "Imprecise Longitude"),
            timeUploaded: this.extractValue(accountHistoryImpreciseLastKnownLocationStringMapData, "Time Uploaded"),
        };
        // read security_and_login_information/login_and_account_creation/login_activity.json
        const loginActivityJson = await this.getJson(zip, "security_and_login_information/login_and_account_creation/login_activity.json");
        const accountHistoryLoginHistory = loginActivityJson["account_history_login_history"];
        result.loginHistory = accountHistoryLoginHistory.map((x) => {
            return {
                title: x["title"],
                ...this.extractObject(x["string_map_data"]),
            };
        });
        // read security_and_login_information/login_and_account_creation/password_change_activity.json
        const passwordChangeActivityJson = await this.getJson(zip, "security_and_login_information/login_and_account_creation/password_change_activity.json");
        const accountHistoryPasswordChangeHistory = passwordChangeActivityJson["account_history_password_change_history"];
        result.passwordChangeHistory = accountHistoryPasswordChangeHistory.map((x) => this.extractValue(x["string_map_data"]["Time"]));
        // read security_and_login_information/login_and_account_creation/signup_information.json
        const signupInformationJson = await this.getJson(zip, "security_and_login_information/login_and_account_creation/signup_information.json");
        const accountHistoryRegistrationInfo = signupInformationJson["account_history_registration_info"];
        const accountHistoryRegistrationInfoStringMapData = accountHistoryRegistrationInfo[0]["string_map_data"];
        result.signupInformation = this.extractSignupInformation(accountHistoryRegistrationInfoStringMapData);
        // read your_instagram_activity/comments/post_comments_1.json
        const postCommentsJson = await this.getJson(zip, "your_instagram_activity/comments/post_comments_1.json");
        result.postCommentCount = postCommentsJson.length;
        // read your_instagram_activity/comments/reels_comments.json
        const reelsCommentsJson = await this.getJson(zip, "your_instagram_activity/comments/reels_comments.json");
        const commentsReelsComments = reelsCommentsJson["comments_reels_comments"];
        result.reelsCommentCount = commentsReelsComments.length;
        // read your_instagram_activity/likes/liked_comments.json
        const likedCommentsJson = await this.getJson(zip, "your_instagram_activity/likes/liked_comments.json");
        const commentLikes = likedCommentsJson["likes_comment_likes"];
        result.likedCommentCount = commentLikes.length;
        // read your_instagram_activity/likes/liked_posts.json
        const likedPostsJson = await this.getJson(zip, "your_instagram_activity/likes/liked_posts.json");
        const mediaLikes = likedPostsJson["likes_media_likes"];
        result.likedPostCount = mediaLikes.length;
        // read your_instagram_activity/story_sticker_interactions/polls.json
        const pollsJson = await this.getJson(zip, "your_instagram_activity/story_sticker_interactions/polls.json");
        const storyActivitiesPolls = pollsJson["story_activities_polls"];
        result.pollCount = storyActivitiesPolls.length;
        // read your_instagram_activity/story_sticker_interactions/questions.json
        const questionsJson = await this.getJson(zip, "your_instagram_activity/story_sticker_interactions/questions.json");
        const storyActivitiesQuestions = questionsJson["story_activities_questions"];
        result.storyQuestionCount = storyActivitiesQuestions.length;
        // read your_instagram_activity/story_sticker_interactions/quizzes.json
        const quizzesJson = await this.getJson(zip, "your_instagram_activity/story_sticker_interactions/quizzes.json");
        const storyActivitiesQuizzes = quizzesJson["story_activities_quizzes"];
        result.quizzCount = storyActivitiesQuizzes.length;
        // read your_instagram_activity/story_sticker_interactions/story_likes.json
        const storyLikesJson = await this.getJson(zip, "your_instagram_activity/story_sticker_interactions/story_likes.json");
        const storyActivitiesStoryLikes = storyLikesJson["story_activities_story_likes"];
        result.storyLikeCount = storyActivitiesStoryLikes.length;
        // read ads_information/ads_and_topics/posts_viewed.json
        const postsViewed = await this.getJson(zip, "ads_information/ads_and_topics/posts_viewed.json");
        const impressionsHistoryPostsSeen = postsViewed["impressions_history_posts_seen"];
        result.advertisementPostsSeenCount = impressionsHistoryPostsSeen.length;
        // read ads_information/ads_and_topics/videos_watched.json
        const videosWatchedJson = await this.getJson(zip, "ads_information/ads_and_topics/videos_watched.json");
        const impressionsHistoryVideosWatched = videosWatchedJson["impressions_history_videos_watched"];
        result.advertisementVideosWatchedCount = impressionsHistoryVideosWatched.length;
        // read ads_information/instagram_ads_and_businesses/advertisers_using_your_activity_or_information.json
        const advertisersUsingYourActivityOrInformation = await this.getJson(zip, "ads_information/instagram_ads_and_businesses/advertisers_using_your_activity_or_information.json");
        const igCustomAudiencesAllTypes = advertisersUsingYourActivityOrInformation["ig_custom_audiences_all_types"];
        result.advertisersUsingYourActivityOrInformation = igCustomAudiencesAllTypes.map((x) => x["advertiser_name"]);
        // read ads_information/instagram_ads_and_businesses/subscription_for_no_ads.json
        const subscriptionForNoAdsJson = await this.getJson(zip, "ads_information/instagram_ads_and_businesses/subscription_for_no_ads.json");
        result.subscriptionForNoAdsStatus = subscriptionForNoAdsJson["label_values"][0]["value"];
        return result;
    }
}
//registerExtractor(InstagramExtractor.name, new InstagramExtractor());
